import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createContext } from "react";
import { DRAWER_NAMES, MONOGRAM_POSITION_STYLES, OVERLAYS } from "./const";
import { useFormValidation } from "../util/useFormValidation";
import {
  uuidv4,
  addItemsToCart,
  useMonogramBounds,
  getParamFromUrl,
  customer_ID,
  removeExistingItems,
  useSVGBounds,
} from "./util";
import { generateAndUploadScreenshot } from "./utils/screenshot";
import { saveItem } from "../util/saveAPI";

const Context = createContext();

const MonogramContext = ({ data, children }) => {
  const [open, setOpen] = useState(false);
  const initialPageLoad = useRef(false);
  const [drawer, setDrawer] = useState(DRAWER_NAMES.Words);
  const [overlay, setOverlay] = useState(OVERLAYS.Monogram);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [drawerFonts, setDrawerFonts] = useState(
    data.fonts.filter((font) => font.available_on.includes(drawer))
  );
  const [chosenFont, setChosenFont] = useState(data.fonts[0]);
  const [userActions, setUserActions] = useState({
    hasSelectedVariant: false,
    hasSelectedThreadColor: false,
    hasSelectedOutlineColor: !chosenFont?.allow_border_color,
    hasSelectedDropShadowColor: !chosenFont?.allow_drop_shadow,
    hasSelectedDecorativeColor: !chosenFont?.decorative_svg,
  });
  const [chosenVariant, setChosenVariant] = useState(data.variants[0]);
  const [positionName, setPositionName] = useState(
    data?.monogram_default_position || Object.keys(MONOGRAM_POSITION_STYLES)[0]
  );
  const [textValue, setTextValue] = useState("");
  const [previousTextValue, setPreviousTextValue] = useState("");
  const [inputRules, setInputRules] = useState();
  const [saveInProgress, setSaveInProgress] = React.useState(false);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [addToCartInProgress, setAddToCartInProgress] = React.useState(false);
  const [loading, setLoading] = useState(false);

  const fontsPerDrawer = useMemo(() => {
    const fonts = {};
    data.fonts.forEach((font) => {
      // if (font.enabled) {
      for (const drawer of font.available_on) {
        if (!fonts[drawer]) {
          fonts[drawer] = [];
        }
        fonts[drawer].push(font);
      }
      // }
    });
    return fonts;
  }, [data.fonts]);

  /**
   * Get the thread color swatch for a given color, has fallback if no swatch is created yet
   *
   * @param {string} color
   * @returns {object} swatch
   */
  const getThreadColorSwatch = useCallback(
    (color) => {
      return (
        data.thread_color_swatches.find(
          (swatch) => swatch?.thread_color_name?.toLowerCase() === color?.toLowerCase()
        ) || { thread_color_name: color }
      );
    },
    [data.thread_color_swatches]
  );
  const [chosenThreadColor, setChosenThreadColor] = useState();
  const [chosenDecorativeColor, setChosenDecorativeColor] = useState();
  const [chosenOutlineColor, setChosenOutlineColor] = useState();
  const [chosenDropShadowColor, setChosenDropShadowColor] = useState();

  const { errors, validate } = useFormValidation([
    {
      name: "textValue",
      value: textValue,
      ...inputRules,
    },
  ]);

  const calibrating = useMemo(() => {
    return window.location.search.includes("calibrate=1");
  }, [window.location.search]);
  const monogramBounds = useMonogramBounds(
    drawer == DRAWER_NAMES.Initials
      ? {
          bounds: data.monogram_bounding_rectangle_2 || data.monogram_bounding_rectangle,
          bound_height: Number(
            data.monogram_bound_height_inches_2 || data.monogram_bound_height_inches
          ),
          monogram_height: Number(chosenFont.font_height_inches),
          calibrating,
        }
      : {
          bounds: data.monogram_bounding_rectangle,
          bound_height: Number(data.monogram_bound_height_inches),
          monogram_height: Number(chosenFont.font_height_inches),
          calibrating,
        }
  );

  const svgBounds = useSVGBounds({
    chosenFont,
    font_height: Number(chosenFont.font_height_inches),
    calibrating,
  });

  const position = useMemo(() => MONOGRAM_POSITION_STYLES[positionName], [positionName]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Set chosen variant from url params
    const variantId = parseInt(urlParams.get("variant"));
    if (variantId) {
      const variant = data.variants.find((variant) => variant.id === parseInt(variantId));
      if (variant) {
        setChosenVariant(variant);
      }
    }

    const initialDrawer = urlParams.get("drawer");
    if (initialDrawer) {
      if (
        !Object.values(DRAWER_NAMES).includes(initialDrawer) ||
        initialDrawer === DRAWER_NAMES.Calibration
      ) {
        return;
      }
      setDrawer(initialDrawer);
    }
    if (open) {
      initialPageLoad.current = true;
    } else {
      initialPageLoad.current = false;
    }
  }, [open]);

  // Open on page load
  useLayoutEffect(() => {
    if (window.location.search.includes("view=monogram")) {
      setOpen(true);
      document.body.classList.remove("monogram-loading");
    }
  }, []);

  // Make sure defaults are set on drawer change
  useEffect(() => {
    if (drawer === DRAWER_NAMES.Calibration) return;

    const initialFontStyle = getParamFromUrl("font")
      ? decodeURIComponent(getParamFromUrl("font"))
      : null;
    const initialThreadColor = getParamFromUrl("thread")
      ? decodeURIComponent(getParamFromUrl("thread"))
      : null;

    const initialDecorativeColor = getParamFromUrl("decorative")
      ? decodeURIComponent(getParamFromUrl("decorative"))
      : null;

    const initialOutlineColor = getParamFromUrl("outline")
      ? decodeURIComponent(getParamFromUrl("outline"))
      : null;

    const initialDropShadowColor = getParamFromUrl("dropshadow")
      ? decodeURIComponent(getParamFromUrl("dropshadow"))
      : null;

    // Added only to test save for later in prod
    if (window.location.search.includes("coming_soon=true")) {
      data.variants.forEach((variant) => {
        variant.is_coming_soon = true;
      });
    }

    const drawerFonts = data.fonts.filter((font) => font.available_on.includes(drawer));
    let foundFont =
      drawerFonts.find((font) => font.font_name === chosenFont.font_name) || drawerFonts[0];
    if (initialFontStyle && drawerFonts.find((font) => font.font_name === initialFontStyle)) {
      foundFont = drawerFonts.find((font) => font.font_name === initialFontStyle);
    }
    let foundThreadColor =
      foundFont.thread_colors.find(
        (threadColor) => threadColor === chosenThreadColor?.thread_color_name
      ) || foundFont.thread_colors[0];

    if (
      initialThreadColor &&
      foundFont.thread_colors.find((threadColor) => threadColor === initialThreadColor)
    ) {
      foundThreadColor = foundFont.thread_colors.find(
        (threadColor) => threadColor === initialThreadColor
      );
    }

    let foundDecorativeColor =
      foundFont.thread_colors.find(
        (decorativeColor) => decorativeColor === chosenDecorativeColor?.thread_color_name
      ) || foundFont.thread_colors[0];

    if (
      initialDecorativeColor &&
      foundFont.thread_colors.find((decorativeColor) => decorativeColor === initialDecorativeColor)
    ) {
      foundDecorativeColor = foundFont.thread_colors.find(
        (decorativeColor) => decorativeColor === initialDecorativeColor
      );
    }

    let foundOutlineColor =
      foundFont.thread_colors.find(
        (borderColor) => borderColor === chosenOutlineColor?.thread_color_name
      ) || foundFont.thread_colors[0];
    if (
      initialOutlineColor &&
      foundFont.thread_colors.find((borderColor) => borderColor === initialOutlineColor)
    ) {
      foundOutlineColor = foundFont.thread_colors.find(
        (borderColor) => borderColor === initialOutlineColor
      );
    }

    let foundDropShadowColor =
      foundFont.drop_shadow_colors.find(
        (shadowColor) => shadowColor === chosenDropShadowColor?.thread_color_name
      ) || foundFont.drop_shadow_colors[0];

    if (
      initialDropShadowColor &&
      foundFont.drop_shadow_colors.find((shadowColor) => shadowColor === initialDropShadowColor)
    ) {
      foundDropShadowColor = foundFont.drop_shadow_colors.find(
        (shadowColor) => shadowColor === initialDropShadowColor
      );
    }

    setDrawerFonts(drawerFonts);
    setChosenFont(foundFont);
    setChosenThreadColor(getThreadColorSwatch(foundThreadColor));
    setChosenDecorativeColor(getThreadColorSwatch(foundDecorativeColor));
    setChosenOutlineColor(getThreadColorSwatch(foundOutlineColor));
    setChosenDropShadowColor(getThreadColorSwatch(foundDropShadowColor));

    if (!initialPageLoad.current) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("drawer", drawer);
    window.history.replaceState({}, "", `${window.location.pathname}?${urlParams}`);
  }, [drawer]);

  useEffect(() => {
    if (!chosenDropShadowColor?.thread_color_name) {
      setChosenDropShadowColor(getThreadColorSwatch(chosenFont.drop_shadow_colors[0]));
    }
  }, [chosenFont]);

  useEffect(() => {
    setUserActions({
      ...userActions,
      hasSelectedVariant: !!getParamFromUrl("variant"),
      hasSelectedThreadColor: !!getParamFromUrl("thread"),
      hasSelectedDecorativeColor: !chosenFont?.decorative_svg || !!getParamFromUrl("decorative"),
      hasSelectedDropShadowColor: !chosenFont?.allow_drop_shadow || !!getParamFromUrl("dropshadow"),
      hasSelectedOutlineColor: !chosenFont?.allow_border_color || !!getParamFromUrl("outline"),
    });
  }, [drawer, chosenFont]);

  useEffect(() => {
    // console.log("drawerFonts", drawerFonts);
    // console.log("chosenVariant", chosenVariant); data.fonts.filter((font) => font.available_on.includes(drawer) && font.enabled)
    // console.log(drawerFonts.filter((font) => font.available_on_product_handles.includes(chosenVariant.product_handle)))
    const fonts = data.fonts.filter(
      (font) =>
        font.available_on.includes(drawer) &&
        (font.available_on_product_handles?.includes(chosenVariant.product_handle) ||
          chosenVariant.collections?.some((collectionId) =>
            font.available_on_collection_ids?.includes(collectionId)
          ))
    );
    setDrawerFonts(fonts.length > 0 ? fonts : drawerFonts);
  }, [chosenVariant, drawer]);

  // Make sure view gets removed from url params when closing
  const handleSetOpen = useCallback((newOpen) => {
    newOpen && setOpen(newOpen);

    if (!newOpen) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete("view");
      window.location.href = `${window.location.pathname}?${urlParams}`;
    }
  }, []);

  useEffect(() => {
    if (!chosenFont?.decorative_svg) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete("decorative");
      window.history.replaceState({}, "", `${window.location.pathname}?${urlParams}`);
    }
    if (chosenFont?.allow_upload) {
      setTextValue("SCL");
    }
  }, [chosenFont]);

  const handleAddToCart = useCallback(async () => {
    if (addToCartInProgress) return;
    setAddToCartInProgress(true);
    const monogramId = uuidv4();
    const fee_variant = chosenFont.monogram_fee_2x
      ? data.fee_product_2x.variants[0]
      : data.fee_product.variants[0];
    const selectedVariants = [chosenVariant, fee_variant];

    const stamp = getParamFromUrl("timestamp") || Math.round(new Date().getTime() / 1000);

    const screenshot = await generateAndUploadScreenshot();
    const monogramUrl = window.location.href.replace(/&timestamp=[^&]*/, "");

    const cartVariants = selectedVariants.map((variant) => {
      const properties = {
        ...variant.properties,
        _monogram_id: monogramId,
        _Timestamp: stamp,
        _Screenshot: screenshot,
        "Monogram Font": chosenFont.font_name,
        "Monogram Position": positionName,
        "Monogram Thread Color": chosenThreadColor?.thread_color_name,
        "Monogram Url": monogramUrl,
        _LPROP: "Final Sale",
        ...(uploadedFile && chosenFont?.allow_upload
          ? {
              "__C Photo": uploadedFile,
            }
          : {
              "Monogram Text": textValue,
            }),
        ...(variant.id === chosenVariant.id
          ? {
              _monogram_main_product: "true",
            }
          : {}),
      };

      if (
        chosenFont?.decorative_svg &&
        chosenDecorativeColor?.thread_color_name !== "" &&
        chosenDecorativeColor?.thread_color_name !== undefined
      ) {
        properties["Monogram Decorative Color"] = chosenDecorativeColor?.thread_color_name;
      }

      if (chosenOutlineColor?.thread_color_name && chosenFont?.allow_border_color) {
        properties["Monogram Outline Color"] = chosenOutlineColor?.thread_color_name;
      }

      if (chosenDropShadowColor?.thread_color_name && chosenFont?.allow_drop_shadow) {
        properties["Monogram Drop Shadow Color"] = chosenDropShadowColor?.thread_color_name;
      }

      return {
        quantity: 1,
        id: variant.id,
        properties,
      };
    });

    await removeExistingItems(stamp);
    await addItemsToCart(cartVariants)
      .then(() => {
        setAddToCartInProgress(false);
      })
      .catch((err) => {
        console.error(err);
        setAddToCartInProgress(false);
      });

    const minicart = document.querySelector("[data-mini-cart-panel]");
    const minicartPanel = new window.Panel({
      el: document.querySelector("[data-mini-cart-panel]"),
    });
    const minicartOpenEvent = new CustomEvent("openMiniCart");

    // setOpen(false);
    minicart.dispatchEvent(minicartOpenEvent);
    minicartPanel.open();
  }, [
    chosenVariant,
    chosenFont,
    positionName,
    textValue,
    chosenThreadColor,
    chosenDecorativeColor,
    chosenOutlineColor,
  ]);

  const handleSaveForLater = useCallback(async () => {
    if (saveInProgress || !chosenVariant) return;
    if (!data?.customerId) {
      setShowLoginModal(true);
      return;
    }
    setSaveInProgress(true);

    const screenshot = await generateAndUploadScreenshot();
    const encodedUrl = window.location.href;

    const variables = {
      name: `${data.product.title} - ${chosenVariant.title}`,
      customizer_link: encodedUrl,
      customer_id: data?.customerId,
      screenshot_link: screenshot,
    };
    const response = await saveItem(variables)
      .then(() => {
        window.location.href = "/pages/wishlist-1?customizations=true";
      })
      .catch((err) => {
        console.error(err);
      });
    setSaveInProgress(false);
  }, [
    chosenVariant,
    chosenFont,
    positionName,
    textValue,
    chosenThreadColor,
    chosenDecorativeColor,
    chosenOutlineColor,
  ]);

  const handleContinue = useCallback(() => {
    if (validate()) {
      chosenVariant?.is_coming_soon ? handleSaveForLater() : handleAddToCart();
    }
  }, [validate, handleAddToCart]);

  const handleCloseAttempt = useCallback(() => {
    if (overlay === OVERLAYS.Exit) {
      handleSetOpen(false);
    } else {
      setOverlay(OVERLAYS.Exit);
    }
  }, [overlay]);

  useEffect(() => {
    if (errors.textValue) {
      validate();
    } else if (textValue.length > 0 && initialPageLoad.current) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("text", textValue);
      window.history.replaceState({}, "", `${window.location.pathname}?${urlParams}`);
    }
  }, [textValue, errors.textValue]);

  return (
    <Context.Provider
      value={{
        monogramDefaultPosition: data.monogram_default_position,
        chosenFont,
        setChosenFont,
        open,
        setOpen: handleSetOpen,
        products: data.products,
        product: data.product,
        fonts: data.fonts,
        chosenVariant,
        setChosenVariant,
        variants: data.variants,
        position,
        positionName,
        setPositionName,
        monogramDefaultPosition: data.monogram_default_position,
        textValue,
        setTextValue,
        previousTextValue,
        setPreviousTextValue,
        drawer,
        setDrawer,
        overlay,
        setOverlay,
        chosenThreadColor,
        setChosenThreadColor,
        threadColorSwatches: data.thread_color_swatches,
        getThreadColorSwatch,
        chosenDecorativeColor,
        setChosenDecorativeColor,
        chosenOutlineColor,
        setChosenOutlineColor,
        chosenDropShadowColor,
        setChosenDropShadowColor,
        logo: data.logo,
        errors,
        validate,
        handleContinue,
        monogramBounds,
        svgBounds,
        calibrating,
        data,
        drawerFonts,
        language: data.language,
        inputRules,
        setInputRules,
        handleCloseAttempt,
        initialPageLoad,
        saveInProgress,
        showLoginModal,
        setShowLoginModal,
        addToCartInProgress,
        loading,
        fontsPerDrawer,
        setLoading,
        userActions,
        setUserActions,
        uploadedFile,
        setUploadedFile,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const withMonogramContext = (Rendered) => (props) =>
  <Context.Consumer>{(value) => <Rendered {...props} {...value} />}</Context.Consumer>;

export default MonogramContext;
